import { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { isBlocked, type PauseAdvisory } from '../model/dayConflicts'
import { ConflictWarning } from '../../../shared/ui/feedback/ConflictWarning/ConflictWarning'

const POPOVER_WIDTH = 240
/**
 * What the popover is assumed to need before it has rendered, used only to pick a side.
 *
 * It is a guess and always was — the box grows by a paragraph for every conflict it has to report,
 * and there is no measuring pass here. 250 covers the plain case at the 44px controls below (title,
 * two time fields, two buttons). Anything taller than the guess is handled by `maxHeight` rather
 * than by making the guess bigger: the popover is clamped to the space that actually exists under
 * the side it chose, and scrolls inside it. On a phone that is the difference between a warning you
 * can read and one rendered past the bottom of the screen.
 */
const POPOVER_APPROX_HEIGHT = 250
const MOBILE_BREAKPOINT = 540

interface Props {
  anchorRect: DOMRect
  startIso: string
  endIso: string
  /**
   * What stands in the way. Any of the three disables Speichern — a Handwerker gets no "trotzdem"
   * on their own calendar, so the answer to a taken slot is to pick another time.
   *
   * Passed as one object rather than three props because `isBlocked` is what gates the button, and
   * splitting them invites a caller to check two of the three.
   */
  advisory: PauseAdvisory
  /** Why the server refused a range this popover had cleared. Only reachable through a race. */
  rejection?: string | null
  isPending: boolean
  /** When set, end = start + fixedDurationMin. The "Bis" field becomes read-only. */
  fixedDurationMin?: number
  onChange: (startIso: string, endIso: string) => void
  onSave: () => void
  onCancel: () => void
}

function toTimeValue(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function applyTime(baseIso: string, timeValue: string): string {
  const [h, m] = timeValue.split(':').map(Number)
  const d = new Date(baseIso)
  d.setHours(h, m, 0, 0)
  return d.toISOString()
}

interface Position {
  top: number
  left: number
  width: number
  /** Room actually left below `top`. The box scrolls inside this rather than past the screen. */
  maxHeight: number
}

function computePosition(anchor: DOMRect): Position {
  const vw = window.innerWidth
  const vh = window.innerHeight

  // Mobile: center horizontally, pin below (or above) the tapped block
  if (vw < MOBILE_BREAKPOINT) {
    const margin = 12
    const width = Math.min(POPOVER_WIDTH, vw - margin * 2)
    const left = Math.round((vw - width) / 2)

    // Prefer below the block; flip above if not enough space
    let top = anchor.bottom + 8
    if (top + POPOVER_APPROX_HEIGHT > vh - margin) {
      top = anchor.top - POPOVER_APPROX_HEIGHT - 8
    }
    top = Math.max(margin, Math.min(top, vh - POPOVER_APPROX_HEIGHT - margin))

    return { top, left, width, maxHeight: vh - top - margin }
  }

  // Desktop/tablet: right or left of block
  let left = anchor.right + 8
  if (left + POPOVER_WIDTH > vw - 8) {
    left = anchor.left - POPOVER_WIDTH - 8
  }
  left = Math.max(8, Math.min(left, vw - POPOVER_WIDTH - 8))

  let top = anchor.top
  if (top + POPOVER_APPROX_HEIGHT > vh - 8) {
    top = vh - POPOVER_APPROX_HEIGHT - 8
  }
  top = Math.max(8, top)

  return { top, left, width: POPOVER_WIDTH, maxHeight: vh - top - 8 }
}

export function PausePopover({
  anchorRect, startIso, endIso, advisory, rejection, isPending,
  fixedDurationMin, onChange, onSave, onCancel,
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const { top, left, width, maxHeight } = computePosition(anchorRect)

  // Close on ESC or outside click
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel()
    }
    function handlePointer(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onCancel()
      }
    }
    document.addEventListener('keydown', handleKey)
    document.addEventListener('pointerdown', handlePointer, true)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('pointerdown', handlePointer, true)
    }
  }, [onCancel])

  const startValue = toTimeValue(startIso)
  const endValue = toTimeValue(endIso)
  // One source for "is this allowed", shared with the drop handler that decides whether to send.
  const canSave = !isBlocked(advisory) && !isPending

  const content = (
    <div
      ref={ref}
      className="fixed bg-white rounded-xl shadow-xl border border-gray-200 z-[9999] overflow-y-auto overscroll-contain"
      style={{ top, left, width, maxHeight }}
    >
      <div className="px-4 pt-3 pb-2">
        <p className="text-sm font-semibold text-gray-800 mb-3">Pause verschieben</p>

        <div className="space-y-2 mb-3">
          <label className="flex flex-col gap-0.5">
            <span className="text-xs text-gray-500">Von</span>
            <input
              type="time"
              step="900"
              value={startValue}
              className="w-full min-h-[44px] text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              onChange={(e) => {
                if (!e.target.value) return
                const newStart = applyTime(startIso, e.target.value)
                if (fixedDurationMin !== undefined) {
                  // End is always start + fixed duration
                  const newEnd = new Date(new Date(newStart).getTime() + fixedDurationMin * 60_000).toISOString()
                  onChange(newStart, newEnd)
                } else {
                  onChange(newStart, endIso)
                }
              }}
            />
          </label>

          <label className="flex flex-col gap-0.5">
            <span className="text-xs text-gray-500">Bis</span>
            <input
              type="time"
              step="900"
              value={endValue}
              readOnly={fixedDurationMin !== undefined}
              className={`w-full min-h-[44px] text-sm border rounded-lg px-2 py-1.5 focus:outline-none ${
                fixedDurationMin !== undefined
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-default'
                  : 'border-gray-300 focus:ring-2 focus:ring-blue-400'
              }`}
              onChange={
                fixedDurationMin !== undefined
                  ? undefined
                  : (e) => {
                      if (!e.target.value) return
                      const newEnd = applyTime(endIso, e.target.value)
                      onChange(startIso, newEnd)
                    }
              }
            />
            {fixedDurationMin !== undefined && (
              <span className="text-[10px] text-gray-400">Pause ist immer {fixedDurationMin} Min.</span>
            )}
          </label>
        </div>

        {/* A refusal the client could not predict — a colleague took the slot first. */}
        {rejection && (
          <p className="mb-2 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] leading-tight text-red-700">
            {rejection}
          </p>
        )}

        {(advisory.nonWorkingDay || advisory.outsideShift) && (
          <p className="mb-2 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] leading-tight text-red-700">
            {advisory.nonWorkingDay
              ? 'An diesem Tag arbeiten Sie nicht. Bitte wählen Sie eine andere Zeit.'
              : 'Außerhalb Ihrer Arbeitszeit. Bitte wählen Sie eine andere Zeit.'}
          </p>
        )}

        {advisory.conflicts.length > 0 && (
          <div className="mb-2">
            <ConflictWarning conflicts={advisory.conflicts} />
          </div>
        )}

        {/* 44px rows: this popover is opened by dragging a block with a thumb, so it is answered
            with one too. `text-xs py-1.5` put both buttons at 26px, well under the tap floor the
            blocks that open it already keep. */}
        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 min-h-[44px] text-sm rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            onClick={onCancel}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className={`flex-1 min-h-[44px] text-sm rounded-lg font-semibold transition-colors ${
              canSave
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            disabled={!canSave}
            onClick={onSave}
          >
            {isPending ? '…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(content, document.body)
}
